import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Popover,
  PopoverTrigger,
  Avatar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Button,
} from '@xstore/shared/ui';
import {
  Menu,
  Command,
  Bot,
  LogOut,
  User,
  Bell,
  Plus,
  Zap,
  Check,
  ChevronDown,
  Search,
  Settings,
  Shield,
  Palette,
  Keyboard,
  Building2,
  CheckCheck,
  Inbox,
  Radio,
  ShoppingCart,
  Workflow,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';

import { ThemeToggle } from '../theme-toggle';
import { useAuth } from '../../providers/auth-provider';
import { useBots } from '../../providers/bot-provider';
import type { Bot as BotData } from '../../providers/bot-provider';
import {
  PopoverPanel,
  SurfaceHeader,
  SurfaceSection,
  SurfaceDivider,
  MenuItem,
  IconButton,
  EmptyState,
  StatusDot,
  ShortcutHint,
  type StatusColor,
} from '../ui/topbar-surface';

interface TopBarProps {
  onMenuClick: () => void;
  onCmdkClick: () => void;
}

export type { TopBarProps };

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface NotificationItem {
  id: string;
  title: string;
  time: string;
  unread: boolean;
  category: 'system' | 'order' | 'bot' | 'security' | 'marketing';
  priority: 'low' | 'normal' | 'high';
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function modeLabel(mode: BotData['mode']) {
  return mode.charAt(0) + mode.slice(1).toLowerCase();
}

function environmentLabel() {
  return import.meta.env.PROD ? 'Production' : 'Development';
}

function botUsername(bot: BotData) {
  return bot.telegramUsername ?? `@${bot.name.toLowerCase().replace(/\s+/g, '_')}`;
}

/* ------------------------------------------------------------------ */
/* Mock data                                                            */
/* ------------------------------------------------------------------ */

const NOTIFICATIONS: NotificationItem[] = [
  {
    id: '1',
    title: 'New order received',
    time: '2m ago',
    unread: true,
    category: 'order',
    priority: 'normal',
  },
  {
    id: '2',
    title: 'Bot webhook restored',
    time: '15m ago',
    unread: true,
    category: 'bot',
    priority: 'high',
  },
  {
    id: '3',
    title: 'Daily backup completed',
    time: '1h ago',
    unread: false,
    category: 'system',
    priority: 'low',
  },
  {
    id: '4',
    title: 'New login from Chrome',
    time: '3h ago',
    unread: false,
    category: 'security',
    priority: 'normal',
  },
];

const categoryIcon: Record<NotificationItem['category'], LucideIcon> = {
  system: CheckCheck,
  order: ShoppingCart,
  bot: Bot,
  security: Shield,
  marketing: Radio,
};

const categoryColor: Record<NotificationItem['category'], string> = {
  system: 'bg-blue-500/15 text-blue-400',
  order: 'bg-emerald-500/15 text-emerald-400',
  bot: 'bg-[#2AABEE]/15 text-[#2AABEE]',
  security: 'bg-amber-500/15 text-amber-400',
  marketing: 'bg-purple-500/15 text-purple-400',
};

const priorityDot: Record<NotificationItem['priority'], string> = {
  low: 'bg-slate-500',
  normal: 'bg-blue-400',
  high: 'bg-red-500',
};

/* ------------------------------------------------------------------ */
/* Status Indicator                                                     */
/* ------------------------------------------------------------------ */

function StatusIndicator({ bot }: { bot: BotData | null }) {
  const [latency, setLatency] = useState(24);

  useEffect(() => {
    const id = setInterval(() => {
      setLatency((prev) => Math.max(12, Math.min(60, prev + Math.floor(Math.random() * 11) - 5)));
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const isOnline = bot?.mode === 'ONLINE';
  const env = environmentLabel();
  const health = isOnline ? 'Healthy' : 'Degraded';
  const status: StatusColor = isOnline ? 'online' : 'offline';

  return (
    <div className="inline-flex h-7 items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.03] px-2 backdrop-blur-sm">
      <StatusDot color={status} pulse={isOnline} />
      <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-muted">
        <span className="text-foreground">{isOnline ? 'Online' : bot?.mode ?? 'Offline'}</span>
        <span className="mx-1.5 text-white/[0.12]">|</span>
        {health}
      </span>
      <span className="hidden text-[10px] text-muted sm:inline">· {latency}ms</span>
      <span
        className={`hidden rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider sm:inline ${
          env === 'Production' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
        }`}
      >
        {env}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bot Switcher                                                         */
/* ------------------------------------------------------------------ */

function BotSwitcher() {
  const { bots, selectedBot, selectBot } = useBots();
  const [open, setOpen] = useState(false);

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <button
          type="button"
          className="group flex h-9 items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] py-1 pl-2 pr-2 text-left transition-all duration-200 hover:border-white/[0.14] hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2AABEE]/40"
        >
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#2AABEE]/20 to-[#2AABEE]/5 text-[#2AABEE] ring-1 ring-white/[0.08]">
            <Bot size={14} />
          </div>
          <div className="min-w-0 max-w-[120px]">
            <p className="truncate text-[13px] font-semibold leading-tight text-foreground">
              {selectedBot?.name ?? 'X Store Bot'}
            </p>
            <p className="truncate text-[10px] font-medium uppercase tracking-wide text-muted">
              {selectedBot?.mode ? modeLabel(selectedBot.mode) : 'Offline'}
            </p>
          </div>
          <ChevronDown
            size={14}
            className={`shrink-0 text-muted transition-transform duration-200 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>
      </PopoverTrigger>
      <PopoverPanel width="w-[280px]">
        <div className="px-3.5 py-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted">
              Select Bot
            </span>
            <span className="text-[10px] text-muted">{bots.length} connected</span>
          </div>
        </div>
        <div className="space-y-0.5 px-1.5 pb-2">
          {bots.length === 0 ? (
            <EmptyState title="No bots connected" description="Connect a bot to get started." />
          ) : (
            bots.map((bot) => {
              const isSelected = bot.id === selectedBot?.id;
              return (
                <button
                  key={bot.id}
                  type="button"
                  onClick={() => {
                    selectBot(bot);
                    setOpen(false);
                  }}
                  className={`group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2AABEE]/40 ${
                    isSelected
                      ? 'bg-[#2AABEE]/10 text-[#2AABEE]'
                      : 'text-foreground hover:bg-white/[0.05]'
                  }`}
                >
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                      isSelected
                        ? 'bg-[#2AABEE]/15 text-[#2AABEE]'
                        : 'bg-white/[0.05] text-muted'
                    }`}
                  >
                    <Bot size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{bot.name}</p>
                    <p className="truncate text-[11px] text-muted">{botUsername(bot)}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-[10px] text-muted">{bot.workspace ?? 'X Store Workspace'}</span>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                          bot.environment === 'production'
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'bg-amber-500/10 text-amber-400'
                        }`}
                      >
                        {bot.environment ?? 'Production'}
                      </span>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                          bot.mode === 'ONLINE'
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : bot.mode === 'OFFLINE'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-amber-500/10 text-amber-400'
                        }`}
                      >
                        {modeLabel(bot.mode)}
                      </span>
                    </div>
                  </div>
                  {isSelected ? <Check size={14} className="shrink-0 text-[#2AABEE]" /> : null}
                </button>
              );
            })
          )}
        </div>
      </PopoverPanel>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/* Quick Actions - Action Center                                        */
/* ------------------------------------------------------------------ */

function QuickActions() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const actions = useMemo(() => {
    const base = [
      { id: 'create-product', label: 'Create Product', icon: Plus, path: '/products', category: 'Create' },
      { id: 'create-broadcast', label: 'Create Broadcast', icon: Radio, path: '/broadcasts', category: 'Create' },
      { id: 'create-workflow', label: 'Create Workflow', icon: Workflow, path: '/workflows', category: 'Create' },
      { id: 'create-ticket', label: 'Create Ticket', icon: MessageSquare, path: '/tickets', category: 'Create' },
      { id: 'open-orders', label: 'Open Orders', icon: ShoppingCart, path: '/orders', category: 'Navigate' },
      { id: 'open-bot', label: 'Bot Manager', icon: Bot, path: '/bot', category: 'Navigate' },
      { id: 'open-settings', label: 'Settings', icon: Settings, path: '/settings', category: 'Navigate' },
    ];
    return base;
  }, []);

  const filtered = useMemo(() => {
    if (!query) return actions;
    const q = query.toLowerCase();
    return actions.filter((a) => a.label.toLowerCase().includes(q) || a.category.toLowerCase().includes(q));
  }, [actions, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    filtered.forEach((a) => {
      if (!map.has(a.category)) map.set(a.category, []);
      map.get(a.category)!.push(a);
    });
    return map;
  }, [filtered]);

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <IconButton icon={<Zap size={18} />} label="Quick actions" active={open} />
      </PopoverTrigger>
      <PopoverPanel width="w-[320px]">
        <div className="border-b border-white/[0.06] px-3.5 py-2.5">
          <div className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2 focus-within:border-[#2AABEE]/30 focus-within:ring-1 focus-within:ring-[#2AABEE]/20">
            <Search size={16} className="text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search actions..."
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted outline-none"
            />
            <ShortcutHint keys={['⌘', 'K']} />
          </div>
        </div>
        <div className="max-h-[360px] overflow-auto p-1.5">
          {filtered.length === 0 ? (
            <EmptyState title="No actions found" description="Try a different search term." />
          ) : (
            Array.from(grouped.entries()).map(([category, items]) => (
              <SurfaceSection key={category} title={category}>
                {items.map((item) => (
                  <MenuItem
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    onClick={() => {
                      setOpen(false);
                      setQuery('');
                      navigate(item.path);
                    }}
                  />
                ))}
              </SurfaceSection>
            ))
          )}
        </div>
      </PopoverPanel>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/* Notification Center                                                    */
/* ------------------------------------------------------------------ */

function NotificationCenter() {
  const [items, setItems] = useState<NotificationItem[]>(NOTIFICATIONS);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const unreadCount = items.filter((n) => n.unread).length;

  const markAllRead = () => {
    setLoading(true);
    setTimeout(() => {
      setItems((prev) => prev.map((n) => ({ ...n, unread: false })));
      setLoading(false);
    }, 400);
  };

  const markRead = (id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n)));
  };

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <div className="relative">
          <IconButton icon={<Bell size={18} />} label="Notifications" active={open} />
          {unreadCount > 0 ? (
            <span className="absolute right-0.5 top-0.5 flex h-4 min-w-[14px] items-center justify-center rounded-full bg-[#2AABEE] px-1 text-[10px] font-bold text-white shadow-[0_0_8px_rgba(42,171,238,0.45)]">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : null}
        </div>
      </PopoverTrigger>
      <PopoverPanel width="w-[360px]">
        <SurfaceHeader
          title="Notifications"
          description={`You have ${unreadCount} unread message${unreadCount === 1 ? '' : 's'}`}
          action={
            unreadCount > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-muted hover:text-foreground"
                onPress={markAllRead}
                isDisabled={loading}
              >
                {loading ? 'Marking...' : 'Mark all read'}
              </Button>
            ) : undefined
          }
        />
        <div className="max-h-[420px] overflow-auto p-1.5">
          {items.length === 0 ? (
            <EmptyState icon={Inbox} title="No notifications" description="You're all caught up." />
          ) : (
            <div className="space-y-0.5">
              {items.map((n) => {
                const Icon = categoryIcon[n.category];
                return (
                  <button
                    key={n.id}
                    onClick={() => markRead(n.id)}
                    className="group relative flex w-full items-start gap-3 rounded-xl p-2.5 text-left transition-colors hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2AABEE]/40"
                  >
                    {n.unread ? (
                      <span className="absolute left-2 top-3.5 h-1.5 w-1.5 rounded-full bg-[#2AABEE] shadow-[0_0_6px_rgba(42,171,238,0.6)]" />
                    ) : null}
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${categoryColor[n.category]}`}
                    >
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${n.unread ? 'font-semibold text-foreground' : 'font-medium text-foreground/90'}`}>
                          {n.title}
                        </p>
                        <span className={`mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full ${priorityDot[n.priority]}`} />
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted">
                        <span className="capitalize">{n.category}</span>
                        <span>·</span>
                        <span>{n.time}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="border-t border-white/[0.06] px-3.5 py-2.5">
          <Button
            fullWidth
            variant="ghost"
            className="h-8 text-xs text-muted hover:text-foreground"
            onPress={() => {}}
          >
            View all notifications
          </Button>
        </div>
      </PopoverPanel>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/* Account Center                                                       */
/* ------------------------------------------------------------------ */

function AccountCenter() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const avatarUrl = `https://api.dicebear.com/9.x/avataaars/svg?seed=${user?.userId ?? 'Admin'}`;

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <button
          type="button"
          className="group relative flex h-8 w-8 items-center justify-center rounded-full ring-1 ring-white/[0.08] transition-all duration-200 hover:ring-[#2AABEE]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2AABEE]/40"
        >
          <Avatar className="h-8 w-8">
            <Avatar.Image src={avatarUrl} />
            <Avatar.Fallback>{(user?.role ?? 'U').slice(0, 2)}</Avatar.Fallback>
          </Avatar>
        </button>
      </PopoverTrigger>
      <PopoverPanel width="w-72">
        <div className="p-3.5">
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11">
              <Avatar.Image src={avatarUrl} />
              <Avatar.Fallback>{(user?.role ?? 'U').slice(0, 2)}</Avatar.Fallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">
                {user?.role ?? 'Administrator'}
              </p>
              <p className="truncate text-xs text-muted">{user?.userId?.slice(0, 18) ?? '—'}</p>
              <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] px-2 py-0.5 text-[10px] text-muted">
                <Building2 size={10} />
                <span className="truncate">X Store Workspace</span>
              </div>
            </div>
          </div>
        </div>
        <SurfaceDivider />
        <SurfaceSection>
          <MenuItem
            icon={User}
            label="Profile"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
          />
          <MenuItem
            icon={Building2}
            label="Workspace Settings"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
          />
          <MenuItem
            icon={Shield}
            label="Security"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
          />
        </SurfaceSection>
        <SurfaceDivider />
        <SurfaceSection title="Preferences">
          <MenuItem
            icon={Palette}
            label="Appearance"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
          />
          <MenuItem
            icon={Keyboard}
            label="Keyboard Shortcuts"
            shortcut="?"
            onClick={() => {
              setOpen(false);
            }}
          />
        </SurfaceSection>
        <SurfaceDivider />
        <div className="p-1.5">
          <MenuItem
            icon={LogOut}
            label="Sign Out"
            danger
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          />
        </div>
      </PopoverPanel>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/* TopBar                                                               */
/* ------------------------------------------------------------------ */

export function TopBar({ onMenuClick, onCmdkClick }: TopBarProps) {
  const { selectedBot } = useBots();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handle = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handle);
    return () => window.removeEventListener('scroll', handle);
  }, []);

  return (
    <header
      className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-white/[0.06] px-3 backdrop-blur-xl transition-all duration-300 sm:px-4"
      style={{
        backgroundColor: isScrolled ? 'rgba(5,7,10,0.88)' : 'rgba(5,7,10,0.55)',
      }}
    >
      {/* Left */}
      <div className="flex flex-1 items-center gap-2 sm:gap-3">
        <IconButton icon={<Menu size={18} />} label="Open menu" onClick={onMenuClick} className="lg:hidden" />

        <Button
          variant="ghost"
          className="hidden h-8 flex-1 items-center justify-start gap-2 rounded-full border border-white/[0.06] bg-white/[0.03] px-3 text-muted transition-colors hover:bg-white/[0.06] sm:flex sm:max-w-[260px] md:flex-initial"
          onPress={onCmdkClick}
        >
          <Command size={14} />
          <span className="text-sm">Search</span>
          <span className="ml-auto text-[11px] text-muted">
            <kbd className="rounded-md bg-white/[0.08] px-1.5 py-0.5">⌘</kbd>
            <kbd className="ml-1 rounded-md bg-white/[0.08] px-1.5 py-0.5">K</kbd>
          </span>
        </Button>
      </div>

      {/* Right */}
      <div className="flex items-center gap-1 sm:gap-2">
        <div className="hidden items-center gap-2 pr-2 md:flex">
          <BotSwitcher />
          <StatusIndicator bot={selectedBot} />
        </div>

        <Tooltip>
          <TooltipTrigger>
            <div className="hidden md:block">
              <ThemeToggle />
            </div>
          </TooltipTrigger>
          <TooltipContent>Toggle theme</TooltipContent>
        </Tooltip>

        <QuickActions />
        <NotificationCenter />
        <AccountCenter />
      </div>
    </header>
  );
}
